import { useEffect, useState } from "react";
import { TrendingUp, Target, DollarSign, Percent, GraduationCap } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, CartesianGrid } from "recharts";
import Layout from "@/components/Layout";
import { PnlCalendar } from "@/components/PnlCalendar";
import { CoachingDigest } from "@/components/CoachingDigest";
import api from "@/lib/api";

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/performance").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const stats = [
    { label: "Win Rate", value: `${data?.win_rate ?? 0}%`, icon: Percent, color: "text-emerald-400" },
    { label: "Total P&L", value: `${data?.total_pnl >= 0 ? "+" : ""}${data?.total_pnl ?? 0}`, icon: DollarSign, color: data?.total_pnl >= 0 ? "text-emerald-400" : "text-red-400" },
    { label: "Closed Trades", value: data?.closed_trades ?? 0, icon: Target, color: "text-cyan-400" },
    { label: "Avg Win / Loss", value: `${data?.avg_win ?? 0} / ${data?.avg_loss ?? 0}`, icon: TrendingUp, color: "text-amber-400" },
    { label: "Avg AI Grade", value: data?.avg_grade ?? "—", icon: GraduationCap, color: gradeColor(data?.avg_grade) },
  ];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp className="w-7 h-7 text-amber-400" />
          <div><h1 className="font-display text-3xl font-bold tracking-tight">Performance Analytics</h1>
            <p className="text-slate-400 text-sm">Track your equity growth, win rate and best assets.</p></div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {stats.map((s) => (
            <div key={s.label} className="glass rounded-xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s|\//g, "-")}`}>
              <s.icon className={`w-5 h-5 mb-2 ${s.color}`} />
              <p className="text-xs uppercase tracking-wider text-slate-400">{s.label}</p>
              <p className={`font-mono-num text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <PnlCalendar daily={data?.daily_pnl || []} />
          <CoachingDigest />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold mb-4">Equity Curve</h3>
            {data?.equity_curve?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.equity_curve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#121824", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                  <Line type="monotone" dataKey="equity" stroke="#10B981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </div>

          <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold mb-4">P&L by Symbol</h3>
            {data?.by_symbol?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_symbol}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="symbol" tick={{ fill: "#64748B", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#121824", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                  <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                    {data.by_symbol.map((e, i) => <Cell key={i} fill={e.pnl >= 0 ? "#10B981" : "#EF4444"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </div>

          <div className="glass rounded-2xl p-6" data-testid="grade-trend-card">
            <h3 className="font-semibold mb-1 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-violet-400" /> AI Grade Trend</h3>
            <p className="text-xs text-slate-500 mb-4">How your trade quality grades evolve over time (F → A+).</p>
            {data?.grade_trend?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.grade_trend.map((g, i) => ({ ...g, idx: i + 1 }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} />
                  <YAxis domain={[1, 6]} ticks={[1, 2, 3, 4, 5, 6]} tickFormatter={(v) => SCORE_TO_GRADE[v]} tick={{ fill: "#64748B", fontSize: 11 }} width={32} />
                  <Tooltip contentStyle={{ background: "#121824", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }}
                    formatter={(v, _n, p) => [`${p.payload.grade} · ${p.payload.symbol}`, "Grade"]} />
                  <Line type="monotone" dataKey="score" stroke="#A78BFA" strokeWidth={2} dot={{ r: 4, fill: "#A78BFA" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <Empty text="No graded trades yet — close trades in the Journal to get AI grades." />}
          </div>

          <div className="glass rounded-2xl p-6" data-testid="grade-distribution-card">
            <h3 className="font-semibold mb-1">Grade Distribution</h3>
            <p className="text-xs text-slate-500 mb-4">Count of trades per AI grade.</p>
            {data?.grade_trend?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.grade_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="grade" tick={{ fill: "#64748B", fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#121824", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {data.grade_distribution.map((e, i) => <Cell key={i} fill={GRADE_HEX[e.grade]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="No graded trades yet — close trades in the Journal to get AI grades." />}
          </div>
        </div>
      </div>
    </Layout>
  );
}

const SCORE_TO_GRADE = { 1: "F", 2: "D", 3: "C", 4: "B", 5: "A", 6: "A+" };
const GRADE_HEX = { "A+": "#10B981", A: "#34D399", B: "#22D3EE", C: "#FBBF24", D: "#FB923C", F: "#EF4444" };
function gradeColor(g) {
  if (!g) return "text-slate-400";
  if (g.startsWith("A")) return "text-emerald-400";
  if (g === "B") return "text-cyan-400";
  if (g === "C") return "text-amber-400";
  return "text-red-400";
}

const Empty = ({ text = "No closed trades yet — log trades in the Journal." }) => <div className="h-[260px] flex items-center justify-center text-slate-500 text-sm text-center px-4">{text}</div>;
